/**
 * URL utility functions for proper URL normalization and joining
 * Ensures URLs never have trailing slashes or double slashes
 */

/**
 * Normalizes a URL by removing trailing slashes and fixing double slashes
 * @param url - The URL to normalize
 * @returns The normalized URL without trailing slash or double slashes
 */
export function normalizeUrl(url: string): string {
  if (!url) return url;

  // Remove trailing slashes
  const _withoutTrailing = url.replace(/\/+$/, '');

  // Fix double slashes (but preserve protocol separators like https://)
  const _normalized = _withoutTrailing.replace(/([^:]\/)\/+/g, '$1');

  return _normalized;
}

/**
 * Joins URL parts ensuring proper single slash separation
 * @param basePart - The base URL part (will be normalized to remove trailing slash)
 * @param pathPart - The path part (will be normalized to remove leading/trailing slashes)
 * @returns The properly joined URL
 */
export function joinUrlParts(basePart: string, pathPart: string): string {
  if (!basePart) return pathPart || '';
  if (!pathPart) return normalizeUrl(basePart);

  // Normalize base part (remove trailing slashes)
  const _normalizedBase = basePart.replace(/\/+$/, '');

  // Normalize path part (remove leading and trailing slashes)
  const _normalizedPath = pathPart.replace(/^\/+/, '').replace(/\/+$/, '');

  // Join with single slash
  if (_normalizedPath) {
    return `${_normalizedBase}/${_normalizedPath}`;
  }

  return _normalizedBase;
}

/**
 * Safely constructs a URL from base and path components
 * @param baseUrl - The base URL
 * @param pathWithQuery - The path and query string
 * @returns A properly constructed URL without trailing slashes or double slashes
 */
export function constructUrl(baseUrl: string, pathWithQuery: string = ''): string {
  const _result = joinUrlParts(baseUrl, pathWithQuery);
  return normalizeUrl(_result);
}

/**
 * Checks if a URL has trailing slashes
 * @param url - The URL to check
 * @returns True if URL ends with one or more slashes
 */
export function hasTrailingSlash(url: string): boolean {
  return /\/+$/.test(url);
}

/**
 * Checks if a URL has double slashes (excluding protocol separators)
 * @param url - The URL to check
 * @returns True if URL contains double slashes that are not part of protocol
 */
export function hasDoubleSlashes(url: string): boolean {
  return /[^:]\/{2,}/.test(url);
}
